import {Component, computed, inject, Signal, signal, WritableSignal} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {AuthenticationService} from '../../../services/authentication.service';
import {ApplicationConfig} from 'frontend/src/app/classes/application-config';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {toSignal} from '@angular/core/rxjs-interop';
import {map, take} from 'rxjs/operators';

export interface EILoginData {
    url: string;
    disabled: boolean;
    locked: boolean;
    username: string;
    eng: string;
}

export const DEFAULT_EI_LOGIN_DATA: EILoginData = {
    url: '',
    disabled: false,
    locked: false,
    username: '',
    eng: '',
};

export enum LoginError {
    DISABLED,
    INACTIVE,
    INVALID,
    VERIFY,
}

@Component({
    selector: 'eaglei-login-form',
    templateUrl: './login-form.component.html',
    styleUrls: ['./login-form.component.scss'],
    standalone: false,
})
export class LoginFormComponent {
    private route = inject(ActivatedRoute);
    private auth = inject(AuthenticationService);
    private router = inject(Router);

    public errorEnum = LoginError;
    public errorCode: WritableSignal<423 | 403 | 401 | 400 | null> = signal(null);
    public error = computed(() => {
        const code = this.errorCode();
        const data = this.eiData();

        if (code === 400) {
            return LoginError.VERIFY;
        }

        if (code === 401) {
            return LoginError.INVALID;
        }

        if (code === 403 || data.disabled) {
            return LoginError.DISABLED;
        }

        if (code === 423 || data.locked) {
            return LoginError.INACTIVE;
        }

        return null;
    });
    public showPassword: boolean = false;

    public eiData: WritableSignal<EILoginData> = signal(DEFAULT_EI_LOGIN_DATA);
    public redirectUrl = computed(() => {
        return (
            'https://eams-auth.oneid.energy.gov/as/authorization.oauth2?response_type=code&client_id=EAGLE-I&redirect_uri=' +
            this.eiData().url +
            '%2Flogin%2Foauth2%2Fcode%2Foneid'
        );
    });

    public formGroup: FormGroup = new FormGroup({
        username: new FormControl<string>(this.eiData().username ?? '', [Validators.required]),
        password: new FormControl<string>('', [Validators.required]),
    });

    protected emailOrUsername: Signal<string> = toSignal<string>(
        this.formGroup.controls.username.valueChanges.pipe(
            map((value: string) => {
                if (value?.includes('@')) return 'email';
                return 'username';
            })
        )
    );

    constructor() {
        this.route.data.pipe(take(1)).subscribe((data: {eaglei: EILoginData}) => {
            this.eiData.set(data.eaglei);

            this.formGroup.controls.username.setValue(this.eiData().username);

            // I would leave this commented out for now because we need to add an error message to the reactivation
            // page when it gets redirected to
            // if (this.eiData().disabled) {
            //   this.goToReactivationPage();
            // }
        });
    }

    /**
     * Tries to login the user with their username and password.
     * If the username and password are valid the user is redirected to the lite map on mobile and landing page otherwise. If the username
     * and password are not valid, an error message is shown.
     */
    public login(): void {
        const {username, password} = this.formGroup.getRawValue();

        this.auth.login(username || ' ', password).subscribe({
            next: (valid: any) => {
                if (valid.status === 200) {
                    if (ApplicationConfig.onMobile()) {
                        // noinspection JSIgnoredPromiseFromCall
                        this.router.navigateByUrl(
                            '/app/lite/map',
                            /* Removed unsupported properties by Angular migration: preserveQueryParams. */ {}
                        );
                    } else {
                        // noinspection JSIgnoredPromiseFromCall

                        this.router.navigateByUrl(
                            '/app/landing',
                            /* Removed unsupported properties by Angular migration: preserveQueryParams. */ {}
                        );
                    }
                } else {
                    this.errorCode.set(valid.status);
                }
            },
            error: (error?: any) => {
                this.errorCode.set(error.status);
            },
        });
    }

    goToReactivationPage() {
        const state = {};
        state[this.emailOrUsername() ?? 'username'] = this.formGroup.controls.username.value || this.eiData().username;

        // noinspection JSIgnoredPromiseFromCall
        this.router.navigate(['login/account-reactivation'], {state});
    }
}
